import { Namespace, TFunction } from "i18next";

import { RateFormProps } from "@/components/Checkout/Steps/RateSelection/RateSelection";
import { listAvailableOptions, useListAvailableOptions } from "@/services/api";
import {
  Basket,
  BasketSubscriberOrderType,
  Charge,
  DiscountDetail,
  Option,
  Product,
  ProductAvailability,
  SimCardDetail,
} from "@/services/model";

import {
  ChargeTypeIDs,
  mobileAbos,
  ServiceIDs,
  TOTAL_DAYS_IN_YEAR,
} from "./constants";
import { TariffTypes } from "./deviceUtils";
import { getProductSpeed, getRoamingDataGb } from "./productMatrix";
import { getChargeString, getCurrency } from "./translationHelpers";
export enum Duration {
  year = "year",
  month = "month",
}
export interface TransformedProduct {
  id?: number;
  bandwidthLabel?: string;
  chargeVoice?: string;
  chargeFlat?: string;
  minContractDur?: number;
  duration?: Duration;
  baseDisplayValue?: string;
  shortName?: string;
  recommended?: boolean;
  availability?: ProductAvailability;
  precharged?: boolean;
  // Linked to the voice product of a data-only product
  voice?: number;
  // Linked to the data product of a voice option
  data?: number;
  isVoiceProduct: boolean;
}
export type TransformedProductsById = { [key: string]: TransformedProduct };
export interface TransformedProducts {
  processedProductsById: TransformedProductsById;
  preSelectedProcessedProduct: TransformedProduct | undefined;
  prechargeVoice: boolean;
}

const isVoiceProduct = (product: Product) => product.productGroup?.id === 1303;

const freeCharge: Charge = { amount: 0, currency: { id: 1 } };

/**
 * Get charge price for data rate from Product
 *
 * chargeType with id 4 is the charge for the flat
 **/
const getDataChargeFromProduct = (
  product: Product,
  precharged: boolean | undefined,
) =>
  getChargeString(
    precharged
      ? freeCharge
      : product.charges?.filter(({ chargeType }) => chargeType?.id === 4)?.[0],
  );

/**
 * Get charge price for data rate from Product
 *
 * chargeType with id 4 is the charge for the flat
 **/
const getVoiceChargeFromProduct = (
  product: Product,
  precharged: boolean | undefined,
) =>
  getChargeString(
    precharged
      ? freeCharge
      : product.charges?.filter(
          ({ chargeType }) => chargeType?.id === ChargeTypeIDs.VoiceOption,
        )?.[0],
  );

export const transformProducts: (
  optionsFromApi: Option[] | undefined,
) => TransformedProducts = (optionsFromApi) => {
  if (!optionsFromApi) {
    return {
      processedProductsById: {},
      preSelectedProcessedProduct: undefined,
      prechargeVoice: false,
    };
  }

  const processedProductsById: TransformedProductsById = {};
  let preSelectedProcessedProduct: TransformedProduct | undefined;
  let prechargedOptionSet = false;
  let prechargeVoice = false;

  const filteredOptions = optionsFromApi.filter(
    ({ product }) =>
      product &&
      product.baseDisplayValue &&
      product.id &&
      product?.productSegment?.id === 4,
  );

  const recommendedSet = new Set(
    optionsFromApi
      .filter((option) => option.recommended)
      .map((option) => option.product?.baseDisplayValue),
  );

  filteredOptions.forEach(({ product, recommended, precharged }) => {
    if (!product?.id) {
      return;
    }
    const isVoice = isVoiceProduct(product);
    const baseDisplayValue = product.baseDisplayValue;
    const isRecommended = recommended || recommendedSet.has(baseDisplayValue);

    const transformedProduct: TransformedProduct = {
      id: product.id,
      bandwidthLabel: product?.bandwidthLabel,
      minContractDur: product?.minContractDur,
      baseDisplayValue,
      shortName: product?.shortName,
      ...getVoiceAndData(product, optionsFromApi),
      duration:
        product?.minContractDur === TOTAL_DAYS_IN_YEAR
          ? Duration.year
          : product?.minContractDur === 30
            ? Duration.month
            : undefined,
      recommended: isRecommended,
      chargeFlat: getDataChargeFromProduct(product, precharged),
      ...(isVoice
        ? { chargeVoice: getVoiceChargeFromProduct(product, precharged) }
        : undefined),
      availability: product.availability,
      precharged,
      isVoiceProduct: isVoice,
    };

    if (isVoice) {
      if (precharged) {
        prechargeVoice = true;
      }
    }

    processedProductsById[product.id] = transformedProduct;

    if (
      (!prechargedOptionSet &&
        isRecommended &&
        transformedProduct.chargeFlat) ||
      precharged
    ) {
      if (precharged) {
        if (!preSelectedProcessedProduct || !prechargedOptionSet || isVoice) {
          prechargedOptionSet = true;
          preSelectedProcessedProduct = transformedProduct;
        }
      } else if (
        !preSelectedProcessedProduct ||
        preSelectedProcessedProduct.duration === Duration.year
      ) {
        preSelectedProcessedProduct = transformedProduct;
      }
    }
  });

  return {
    processedProductsById,
    preSelectedProcessedProduct,
    prechargeVoice,
  };
};

/**
 * Resolves which product to add to the basket on rate selection continue.
 * Precharged physical SIMs that include voice must keep the voice product even
 * when the voice option UI is hidden in onboarding.
 */
export const resolveSelectedOptionForRateSelection = ({
  processedProductsById,
  currentRate,
  prechargeVoice,
}: {
  processedProductsById: TransformedProductsById;
  currentRate: string;
  prechargeVoice: boolean;
}): TransformedProduct | undefined => {
  const dataProduct = processedProductsById[currentRate];
  if (!dataProduct) {
    return undefined;
  }

  if (prechargeVoice && dataProduct.voice) {
    return processedProductsById[dataProduct.voice] ?? dataProduct;
  }

  return dataProduct;
};

export const getSelectedOptionForBasketProduct = async (
  basketId: number,
  productId: number | undefined,
): Promise<TransformedProduct | undefined> => {
  if (!productId) {
    return undefined;
  }

  const options = await listAvailableOptions(basketId, 0);
  const { processedProductsById } = transformProducts(options);

  return processedProductsById[productId];
};

export const combineProductsByBaselabel = (products: TransformedProductsById) =>
  Object.values(products)
    .filter((product) => product.id === product.data)
    .reduce(
      (
        prev: { [key: string]: TransformedProduct[] },
        curr: TransformedProduct,
      ): { [key: string]: TransformedProduct[] } =>
        curr.baseDisplayValue
          ? {
              ...prev,
              [curr.baseDisplayValue]: [
                ...(prev[curr.baseDisplayValue] ?? []),
                curr,
              ],
            }
          : prev,
      {},
    );

export const getNextPaymentDate = (days?: number) => {
  const date = new Date();
  date.setDate(date.getDate() + (days ?? 0));

  return date;
};

/** Days until the next recurring charge shown on subscription-change checkout. */
export const getSubscriptionRecurringDateOffsetDays = ({
  subscriberOrderType,
  remainingDays,
  minContractDurationDays,
}: {
  subscriberOrderType?: string;
  remainingDays?: number;
  minContractDurationDays: number;
}): number | undefined =>
  subscriberOrderType === BasketSubscriberOrderType.DOWNGRADE
    ? remainingDays
    : minContractDurationDays;

export const getPrechargedProductFromOptions = (options: Option[]) =>
  options.find((option) => option.precharged)?.product;

export const useGetPrechargedOptionForBasket = (basket: Basket | undefined) => {
  const { data: availableOptions } = useListAvailableOptions(
    basket?.id || -1,
    0,
    undefined,
    { query: { enabled: !!basket } },
  );
  if (!availableOptions) {
    return undefined;
  }

  return getPrechargedProductFromOptions(availableOptions);
};

export const useHasPrechargedMobileAbo = (basket: Basket | undefined) => {
  const prechargedOption = useGetPrechargedOptionForBasket(basket);

  return (
    !!prechargedOption?.id &&
    mobileAbos.includes(prechargedOption.id.toString())
  );
};

export const getPreselectedOption: (
  basket: Basket | undefined,
) => Promise<RateFormProps> = async (basket) => {
  if (!basket) {
    return { rate: "" };
  }

  const entry = basket?.entries["0"];
  const optionInBasketId = entry?.options?.[0]?.product?.id;

  const options = await listAvailableOptions(
    basket?.id || -1,
    0,
    undefined,
  ).then((res) => transformProducts(res));

  const { preSelectedProcessedProduct, processedProductsById } = options || {};

  // Following part covers the logic necessary to set an option from the basket or a pre-selected flat option
  const preselectionLocal: Partial<RateFormProps> = {
    rate: undefined,
  };
  // Wenn wir noch keine Rate gesetzt, und transformierte Produkte haben...
  if (processedProductsById && Object.keys(processedProductsById).length > 0) {
    // Does set the form state based on the provided product id - but ignores the voice option
    const setRate = (productID: number) => {
      const product = processedProductsById[productID];

      if (!product?.data) {
        return;
      }

      // RateSelection only ever renders monthly rate options (billing period
      // is chosen at Checkout via CheckoutBillingPeriodSwitch), so a yearly
      // product already on the basket must resolve to its monthly peer here
      // or none of the rendered radio options would match it.
      const dataProduct = processedProductsById[String(product.data)];
      const monthlyPeer =
        dataProduct?.duration === Duration.year
          ? findDurationPeer(dataProduct, processedProductsById, Duration.month)
          : undefined;

      preselectionLocal["rate"] = String(monthlyPeer?.data ?? product.data);
    };

    if (preSelectedProcessedProduct) {
      if (
        preSelectedProcessedProduct.precharged &&
        preSelectedProcessedProduct.id !== undefined
      ) {
        // Prio A
        setRate(preSelectedProcessedProduct.id);
      } else {
        if (optionInBasketId) {
          // Prio B
          setRate(optionInBasketId);
        } else {
          // Prio C
          const { id } = preSelectedProcessedProduct;
          if (id) {
            // For a recommended product only the rate should be set, not the voice option.
            setRate(id);
          }
        }
      }
    }
  }

  return {
    rate: "",
    ...preselectionLocal,
  };
};

const getVoiceAndData = (product: Product, optionsFromApi: Option[]) => {
  const isMobileAbo = mobileAbos.includes(`${product.id}`);
  if (isMobileAbo) {
    return {
      voice: product.id,
      data: product.id,
    };
  }
  if (isVoiceProduct(product)) {
    return {
      voice: product.id,
      data: optionsFromApi.filter(
        (value) =>
          value.product?.baseDisplayValue === product.baseDisplayValue &&
          value.product?.minContractDur === product?.minContractDur &&
          product.id !== value.product?.id,
      )[0]?.product?.id,
    };
  } else {
    return {
      voice: optionsFromApi.filter(
        (value) =>
          value.product?.baseDisplayValue === product.baseDisplayValue &&
          value.product?.minContractDur === product?.minContractDur &&
          product.id !== value.product?.id,
      )[0]?.product?.id,
      data: product.id,
    };
  }
};

/**
 * Applies a Friends-with-Benefits tribe discount percentage to a formatted
 * charge value (e.g. "13.– CHF"), returning the discounted, formatted value.
 */
export const getDiscountedValue = ({
  value,
  discountPercent,
}: {
  value?: string;
  discountPercent: number;
}) => {
  if (!value) {
    return;
  }

  const valueAmount = parseFloat(stripCurrencyPrefix(value)?.trim() ?? "");
  const discount = (discountPercent / 100) * valueAmount;

  return getCurrency(valueAmount - discount, "CHF");
};

/**
 * Drops the leading currency code from a formatted charge (e.g. "CHF 13.00"
 * → "13.00"), where the currency is conveyed elsewhere (e.g. a unit caption).
 */
export const stripCurrencyPrefix = (value?: string) =>
  value?.replace(/^[A-Z]{3}\s*/, "");

/**
 * Converts a whole-franc price into the Swiss "4.–" notation (e.g. "4.00" →
 * "4.–"), matching the Figma design. Non-zero decimals are left untouched
 * (e.g. "9.10" stays "9.10").
 */
export const toSwissPriceNotation = (value?: string) =>
  value?.replace(/\.00$/, ".–");

// As Backend sends discount splitted by charges it might be we get more than
// one discount for the same id. So we need to group them accordingly.
export const calculateDiscounts = (discounts?: DiscountDetail[]) => {
  if (!discounts || discounts.length === 0) {
    return []; // Return an empty array if no discounts are provided
  }

  const groupedDiscounts = discounts?.reduce<Record<number, DiscountDetail>>(
    (acc, discount) => {
      // Skip discounts without an id
      if (!discount.id) {
        return acc;
      }

      if (acc[discount.id]) {
        acc[discount.id].amount =
          (acc[discount.id].amount || 0) + (discount.amount || 0);
        acc[discount.id].amountNet =
          (acc[discount.id].amountNet || 0) + (discount.amountNet || 0);
      } else {
        // If this id is new, initialize it in the accumulator
        acc[discount.id] = { ...discount };
      }

      return acc;
    },
    {},
  );

  // Convert the grouped discounts back into an array
  return Object.values(groupedDiscounts);
};

export const getIncludedFeaturesRateSelection = (
  t: TFunction<Namespace, undefined, Namespace>,
  tariffType: TariffTypes,
  minContractDuration: number,
) => {
  // default for data tariff
  if (tariffType === TariffTypes.dataTariff) {
    return [
      {
        name: t("portal:mobileAbo.features.unlimitedData"),
      },
    ];
  }

  const includedFeatures = [];

  // flat home
  if (
    tariffType === TariffTypes.flatHome4G ||
    tariffType === TariffTypes.flatHome5G
  ) {
    // Use specific translation keys for Flat Home speed limits
    const speedTranslationKey =
      tariffType === TariffTypes.flatHome4G
        ? "portal:mobileAbo.features.limitedSpeedFlatHome4G"
        : "portal:mobileAbo.features.limitedSpeedFlatHome5G";

    includedFeatures.push(
      {
        name: t("portal:flatHome.unlimitedDataCH"),
      },
      { name: t(speedTranslationKey) },
      { name: t("portal:flatHome.duration") },
    );

    return includedFeatures;
  } else {
    // default
    includedFeatures.push(
      {
        name: t("portal:mobileAbo.features.unlimitedData"),
      },
      {
        name: t("portal:mobileAbo.features.unlimitedCallsAndSms"),
      },
    );

    if (
      tariffType === TariffTypes.flatMobileSwiss ||
      tariffType === TariffTypes.flatMobile ||
      tariffType === TariffTypes.flatMobilePlus
    ) {
      includedFeatures.push({ name: t("portal:mobileAbo.features.fullSpeed") });
    }

    // Flat Mobile One (legacy id: Swiss)
    if (tariffType === TariffTypes.flatMobileSwiss) {
      includedFeatures.push(
        {
          name: t("portal:mobileAbo.features.roamingEuUsa", {
            volume: "3",
          }),
        },
        {
          name: t("portal:mobileAbo.features.limitedCallEuUsa", {
            volume: minContractDuration === 30 ? "30" : "360",
          }),
        },
      );
    }
    // Flat Mobile Two (legacy id: Flat Mobile)
    if (tariffType === TariffTypes.flatMobile) {
      includedFeatures.push(
        {
          name: t("portal:mobileAbo.features.roamingEuUsa", {
            volume: "20",
          }),
        },
        {
          name: t("portal:mobileAbo.features.limitedCallEuUsa", {
            volume: minContractDuration === 30 ? "100" : "1200",
          }),
        },
      );
    }
    // Flat Mobile Three (legacy id: Plus)
    if (tariffType === TariffTypes.flatMobilePlus) {
      includedFeatures.push(
        {
          name: t("portal:mobileAbo.features.roamingEuUsa", {
            volume: "50",
          }),
        },
        {
          name: t("portal:mobileAbo.features.unlimitedCallEuUsa"),
        },
      );
    }

    return includedFeatures;
  }
};

/**
 * The roaming headline (data volume + calling minutes) and its shared "in
 * EU/USA" zone caption for a rate-selection card, matching the Figma
 * design (node 2710-178004) — the same caption for every Flat Mobile tariff,
 * with the calling minutes folded into the headline rather than shown
 * separately, unlike `getIncludedFeaturesRateSelection`'s per-line features.
 */
export const getRoamingSummaryForRateSelection = (
  t: TFunction<Namespace, undefined, Namespace>,
  tariffType: TariffTypes,
  minContractDuration: number,
) => {
  const caption = t("Onboarding.sections.rate-selection.roamingZone");

  switch (tariffType) {
    case TariffTypes.flatMobileSwiss:
      return {
        headline: t(
          "Onboarding.sections.rate-selection.roamingVolumeWithCalls",
          {
            volume: "3",
            minutes: minContractDuration === 30 ? "30" : "360",
          },
        ),
        caption,
      };
    case TariffTypes.flatMobile:
      return {
        headline: t(
          "Onboarding.sections.rate-selection.roamingVolumeWithCalls",
          {
            volume: "20",
            minutes: minContractDuration === 30 ? "100" : "1200",
          },
        ),
        caption,
      };
    case TariffTypes.flatMobilePlus:
      return {
        headline: t(
          "Onboarding.sections.rate-selection.roamingVolumeWithUnlimitedCalls",
          { volume: "50" },
        ),
        caption,
      };
    default:
      return undefined;
  }
};

/**
 * The Download/Upload speed pair for a data-only rate-selection card,
 * matching the Figma design (node 2608-123251). Both values come from the
 * product matrix so asymmetric products such as Flat 2000 stay accurate.
 */
export const getDataSpeedSummaryForRateSelection = (
  t: TFunction<Namespace, undefined, Namespace>,
  tariffType: TariffTypes,
  productId?: number,
) => {
  if (tariffType !== TariffTypes.dataTariff) {
    return undefined;
  }

  const { download, upload } = getProductSpeed(productId) ?? {};

  if (download === undefined || upload === undefined) {
    return undefined;
  }

  return {
    download: t("Onboarding.sections.rate-selection.mbitPerSecond", {
      value: download,
    }),
    downloadLabel: t("Onboarding.sections.rate-selection.download"),
    upload: t("Onboarding.sections.rate-selection.mbitPerSecond", {
      value: upload,
    }),
    uploadLabel: t("Onboarding.sections.rate-selection.upload"),
  };
};

export interface CheckoutTariffFeatureRow {
  icon: "signal" | "phone";
  title: string;
  subtitle: string;
}

export interface CheckoutTariffFeatureGroups {
  switzerland: CheckoutTariffFeatureRow;
  international: CheckoutTariffFeatureRow[];
}

// Feature rows for the "Tarifoptionen" card in Checkout's Zusammenfassung.
export const getCheckoutTariffFeatureGroups = (
  t: TFunction<Namespace, undefined, Namespace>,
  tariffType: TariffTypes,
  minContractDuration: number,
): CheckoutTariffFeatureGroups => {
  if (tariffType === TariffTypes.dataTariff) {
    return {
      switzerland: {
        icon: "signal",
        title: t(
          "Onboarding.sections.checkout.summary.switzerlandDataOnlyTitle",
        ),
        subtitle: t("Onboarding.sections.checkout.summary.unlimited"),
      },
      international: [],
    };
  }

  const switzerland: CheckoutTariffFeatureRow = {
    icon: "signal",
    title: t("Onboarding.sections.checkout.summary.switzerlandDataTitle"),
    subtitle: t("Onboarding.sections.checkout.summary.unlimited"),
  };

  const international: CheckoutTariffFeatureRow[] = [];

  if (tariffType === TariffTypes.flatMobileSwiss) {
    international.push(
      {
        icon: "signal",
        title: t("Onboarding.sections.checkout.summary.dataRoamingTitle"),
        subtitle: t("Onboarding.sections.checkout.summary.dataRoamingVolume", {
          volume: "3",
        }),
      },
      {
        icon: "phone",
        title: t("Onboarding.sections.checkout.summary.callsTitle"),
        subtitle: t("Onboarding.sections.checkout.summary.callsVolumeMinutes", {
          volume: minContractDuration === 30 ? "30" : "360",
        }),
      },
    );
  }

  if (tariffType === TariffTypes.flatMobile) {
    international.push(
      {
        icon: "signal",
        title: t("Onboarding.sections.checkout.summary.dataRoamingTitle"),
        subtitle: t("Onboarding.sections.checkout.summary.dataRoamingVolume", {
          volume: "20",
        }),
      },
      {
        icon: "phone",
        title: t("Onboarding.sections.checkout.summary.callsTitle"),
        subtitle: t("Onboarding.sections.checkout.summary.callsVolumeMinutes", {
          volume: minContractDuration === 30 ? "100" : "1200",
        }),
      },
    );
  }

  if (tariffType === TariffTypes.flatMobilePlus) {
    international.push(
      {
        icon: "signal",
        title: t("Onboarding.sections.checkout.summary.dataRoamingTitle"),
        subtitle: t("Onboarding.sections.checkout.summary.dataRoamingVolume", {
          volume: "50",
        }),
      },
      {
        icon: "phone",
        title: t("Onboarding.sections.checkout.summary.callsTitle"),
        subtitle: t("Onboarding.sections.checkout.summary.unlimited"),
      },
    );
  }

  return { switzerland, international };
};

// Simple check if tariff name (or other value) includes the 'Flat Mobile' string - if the naming changes in future we only have one place to adapt.
export const isFlatMobileTariff = (value: string) =>
  value.includes("Flat Mobile");

// Check if tariff is a Flat Mobile tariff with international calls included
// (One, Two, and Three — legacy ids Swiss / Flat Mobile / Plus)
export const isFlatMobileInternationalCallsIncluded = (
  serviceId?: ServiceIDs,
) => {
  switch (serviceId) {
    case ServiceIDs.FlatMobileSwissMonth:
    case ServiceIDs.FlatMobileSwissYear:
    case ServiceIDs.FlatMobileMonth:
    case ServiceIDs.FlatMobileYear:
    case ServiceIDs.FlatMobilePlusMonth:
    case ServiceIDs.FlatMobilePlusYear:
      return true;
    default:
      return false;
  }
};

// Check if service/option id is any Flat Mobile tariff (including Swiss, standard, and Plus)
export const isAnyFlatMobileTariff = (id?: number) => {
  if (!id) {
    return false;
  }

  return [
    ServiceIDs.FlatMobileMonth,
    ServiceIDs.FlatMobileYear,
    ServiceIDs.FlatMobilePlusMonth,
    ServiceIDs.FlatMobilePlusYear,
    ServiceIDs.FlatMobileSwissMonth,
    ServiceIDs.FlatMobileSwissYear,
  ].includes(id);
};

// Check if service/option id is one of the Flat Home ids
export const isFlatHomeTariff = (id?: number) =>
  id ? [ServiceIDs.FlatHome4G, ServiceIDs.FlatHome5G].includes(id) : false;

/**
 * Catalogue label for Flat Home. The smartphone options endpoint (131001)
 * often omits these products, so callers that only have a productId need a
 * hard-coded fallback for display (purchase intents, test webshop, etc.).
 */
export const getFlatHomeBaseDisplayValue = (
  productId?: number,
): string | undefined => {
  if (productId === ServiceIDs.FlatHome4G) {
    return "Flat Home 4G";
  }
  if (productId === ServiceIDs.FlatHome5G) {
    return "Flat Home 5G";
  }

  return undefined;
};

/**
 * Finds the monthly/yearly peer of a product by baseDisplayValue.
 * Voice products resolve to the matching-duration voice twin when available.
 */
export const findDurationPeer = (
  current: TransformedProduct | undefined,
  productsById: TransformedProductsById,
  targetDuration: Duration,
): TransformedProduct | undefined => {
  if (!current?.baseDisplayValue) {
    return undefined;
  }

  const dataProductId = current.data ?? current.id;
  const dataProduct = dataProductId
    ? productsById[String(dataProductId)]
    : current;
  const baseLabel = dataProduct?.baseDisplayValue ?? current.baseDisplayValue;

  const peerData = Object.values(productsById).find(
    (product) =>
      product.baseDisplayValue === baseLabel &&
      product.duration === targetDuration &&
      product.id === product.data,
  );

  if (!peerData) {
    return undefined;
  }

  const currentIsVoice =
    current.isVoiceProduct ||
    (current.id !== undefined &&
      current.data !== undefined &&
      current.id !== current.data);

  if (!currentIsVoice) {
    return peerData;
  }

  return (
    Object.values(productsById).find(
      (product) =>
        product.baseDisplayValue === baseLabel &&
        product.duration === targetDuration &&
        product.isVoiceProduct,
    ) ?? peerData
  );
};

/**
 * Rate cards only list monthly data products. Resolves any product (voice twin
 * and/or yearly variant) to the id of the card that represents it.
 */
export const getRateCardProductId = (
  productId: number | undefined,
  productsById: TransformedProductsById,
): number | undefined => {
  if (!productId) {
    return undefined;
  }

  const dataProductId = productsById[productId]?.data ?? productId;
  const dataProduct = productsById[dataProductId];

  if (dataProduct?.duration !== Duration.year) {
    return dataProductId;
  }

  return (
    findDurationPeer(dataProduct, productsById, Duration.month)?.id ??
    dataProductId
  );
};

export const getCallsVolume = ({
  t,
  tariffType,
  minContractDuration,
}: {
  t: TFunction<"portal", undefined, "portal">;
  tariffType: TariffTypes;
  minContractDuration?: number;
}) => {
  switch (tariffType) {
    case TariffTypes.flatMobileSwiss:
      return minContractDuration === TOTAL_DAYS_IN_YEAR
        ? "360 Min."
        : "30 Min.";
    case TariffTypes.flatMobile:
      return minContractDuration === TOTAL_DAYS_IN_YEAR
        ? "1200 Min."
        : "100 Min.";
    case TariffTypes.flatMobilePlus:
      return t("cockpit.managementTile.roaming.showOption.active.unlimited");
    default:
      return undefined;
  }
};

/**
 * Roaming data volume included in a Flat Mobile subscription, in GB.
 * Reads from productMatrix so Mobile Abo card / SIM options stay in sync.
 */
export const getIncludedRoamingVolumeInGb = (serviceId?: number) => {
  const volume = getRoamingDataGb(serviceId);

  return volume === undefined ? undefined : String(volume);
};

export interface SubscriptionSpeeds {
  /** Maximum download speed in Mbit/s. */
  download: number;
  /** Maximum upload speed in Mbit/s. */
  upload: number;
  /** Mobile network generation the subscription runs on, e.g. "5G". */
  network: string;
}

/**
 * Download/upload pair shown as metric tiles in "Dein Abo" for Flat Home and
 * data-only subscriptions. Speeds come from the product matrix so API display
 * values cannot override the catalogue values.
 *
 * Returns undefined for Flat Mobile subscriptions, which list their included
 * options instead of speeds.
 */
export const getSubscriptionSpeeds = (
  simDetail: SimCardDetail,
): SubscriptionSpeeds | undefined => {
  const serviceId = simDetail.serviceID ?? simDetail.successorForRenew?.id;

  if (isAnyFlatMobileTariff(serviceId)) {
    return undefined;
  }

  const { download, upload } = getProductSpeed(serviceId) ?? {};

  if (download === undefined || upload === undefined) {
    return undefined;
  }

  return {
    download,
    upload,
    network: serviceId === ServiceIDs.FlatHome4G ? "4G" : "5G",
  };
};
